import { FiAlertTriangle } from 'react-icons/fi';
import SelectTextField from '../../../molecules/SelectTextField';
import TextEdit from '../../../molecules/TextEdit';
import DateField from '../../../molecules/DateField';
import { getErrorMessage } from '../../../../utils/formErrorUtils';
import {
    LEGAL_MEASURE_STATUS_OPTIONS,
    LegalMeasureStatus,
    LegalMeasureType,
    type SelectOption,
} from '../../../../types/LegalCase';
import type { ErrorField } from '../../../../types/ErrorField';
import useLegalCaseMeasureForm, { residualErrors } from '../../../../hooks/useLegalCaseMeasureForm';
import type { AddMeasureValue } from '../../../../hooks/useLegalCaseMeasures';

interface LegalCaseMeasureFormProps {
    typeOptions: SelectOption[];
    errorFields: ErrorField[];
    isSaving: boolean;
    onSubmit: (value: AddMeasureValue) => Promise<boolean>;
    onCancel: () => void;
}

// Só JSX + bindings: estado e regras do formulário vivem em useLegalCaseMeasureForm.
const LegalCaseMeasureForm = ({
    typeOptions,
    errorFields,
    isSaving,
    onSubmit,
    onCancel,
}: LegalCaseMeasureFormProps) => {
    const form = useLegalCaseMeasureForm({ onSubmit });

    return (
        <div className="rounded-lg border border-gray-200 dark:border-slate-700 p-3 space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <SelectTextField
                    label="Tipo da medida"
                    items={typeOptions}
                    value={form.measureType}
                    // O select só oferece tipos válidos (page-form ou fallback local).
                    onChange={(e) => form.setMeasureType(e.target.value as LegalMeasureType)}
                    errorMessage={getErrorMessage(errorFields, 'measureType')}
                />
                <SelectTextField
                    label="Status"
                    items={LEGAL_MEASURE_STATUS_OPTIONS}
                    value={form.status}
                    onChange={(e) => form.setStatus(e.target.value as LegalMeasureStatus)}
                    errorMessage={getErrorMessage(errorFields, 'status')}
                />
            </div>

            <DateField
                label="Data da solicitação (opcional)"
                value={form.requestedAt}
                onChange={form.setRequestedAt}
                errorMessage={getErrorMessage(errorFields, 'requestedAt')}
            />

            <TextEdit
                label="Observação (opcional)"
                value={form.description}
                onChange={(e) => form.setDescription(e.target.value)}
                errorMessage={getErrorMessage(errorFields, 'description')}
            />

            {/* Erros de validação (422) sem campo próprio na tela — não deixa sumir. */}
            {residualErrors(errorFields).map((e) => (
                <div key={e.fieldName} className="flex items-center gap-2 text-sm text-red-700 dark:text-red-400">
                    <FiAlertTriangle size={14} />
                    <span>{e.message}</span>
                </div>
            ))}

            <div className="flex items-center justify-end gap-2">
                <button
                    type="button"
                    onClick={onCancel}
                    className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg"
                >
                    Cancelar
                </button>
                <button
                    type="button"
                    onClick={form.submit}
                    disabled={isSaving || !form.canSubmit}
                    className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50"
                >
                    {isSaving ? 'Salvando...' : 'Registrar medida'}
                </button>
            </div>
        </div>
    );
};

export default LegalCaseMeasureForm;
